package dev.danko.roblox_craft_ai.uikit.components

import dev.danko.roblox_craft_ai.shared.model.displayTitle
import dev.danko.roblox_craft_ai.uikit.i18n.strings
import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.Resize
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import dev.danko.roblox_craft_ai.shared.model.AssetResponse
import dev.danko.roblox_craft_ai.shared.model.ChatMessage
import dev.danko.roblox_craft_ai.shared.model.ChatMessageRole
import dev.danko.roblox_craft_ai.uikit.theme.LocalAppTheme
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vh
import org.jetbrains.compose.web.css.vw
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.TextArea

val ChatDrawerOverlayStyle = CssStyle {
    base {
        Modifier
            .position(Position.Fixed)
            .top(0.px)
            .left(0.px)
            .width(100.vw)
            .height(100.vh)
            .backgroundColor(Colors.Black.copy(alpha = 140))
            .zIndex(1200)
    }
}

val ChatDrawerContainerStyle = CssStyle {
    base {
        Modifier
            .position(Position.Fixed)
            .top(0.px)
            .right(0.px)
            .width(32.cssRem)
            .maxWidth(100.vw)
            .height(100.vh)
            .boxShadow(0.px, 8.px, 32.px, 0.px, Colors.Black.copy(alpha = 160))
            .zIndex(1201)
            .display(DisplayStyle.Flex)
            .flexDirection(FlexDirection.Column)
    }
}

@Composable
fun AssetChatDrawer(
    visible: Boolean,
    asset: AssetResponse,
    messages: List<ChatMessage>,
    isLoading: Boolean,
    onCloseRequest: () -> Unit,
    onSendMessage: (instruction: String) -> Unit,
    onApplyMessage: (messageId: String) -> Unit
) {
    if (!visible) return

    val theme = LocalAppTheme.current
    val s = strings()
    val palette = theme.colors
    var inputPrompt by remember { mutableStateOf("") }

    val quickPrompts = listOf(
        s.chat.quickOptimize,
        s.chat.quickStrict,
        s.chat.quickBugs,
        s.chat.quickErrors,
        s.chat.quickDocs
    )

    Box(
        ChatDrawerOverlayStyle.toModifier()
            .onClick { onCloseRequest() }
    ) {
        Box(
            ChatDrawerContainerStyle.toModifier()
                .backgroundColor(palette.surface)
                .borderLeft(1.px, LineStyle.Solid, palette.border)
                .onClick { it.stopPropagation() }
        ) {
            // Header
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(1.cssRem, 1.25.cssRem)
                    .borderBottom(1.px, LineStyle.Solid, palette.border)
                    .backgroundColor(palette.background),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Column(modifier = Modifier.flexGrow(1)) {
                    Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.gap(0.5.cssRem)) {
                        SpanText("💬 AI Chat & Refactor", Modifier.fontWeight(FontWeight.Bold).fontSize(1.1.cssRem).color(palette.textPrimary))
                        val currentRev = asset.revisions.maxOfOrNull { it.revisionNumber } ?: 1
                        Div(
                            Modifier
                                .backgroundColor(palette.accent.toRgb().copy(alpha = 40))
                                .color(palette.accent)
                                .padding(0.125.cssRem, 0.5.cssRem)
                                .borderRadius(0.375.cssRem)
                                .fontSize(0.75.cssRem)
                                .fontWeight(FontWeight.SemiBold)
                                .toAttrs()
                        ) {
                            SpanText(s.chat.revisionShort(currentRev))
                        }
                    }
                    SpanText(
                        asset.displayTitle,
                        Modifier.fontSize(0.8.cssRem).color(palette.textSecondary)
                    )
                }

                AppButton(
                    text = "",
                    iconLeft = "✕",
                    variant = AppButtonVariant.OUTLINE,
                    size = AppButtonSize.SM,
                    onClick = onCloseRequest
                )
            }

            // Quick Actions Bar
            Div(
                Modifier
                    .fillMaxWidth()
                    .padding(0.625.cssRem, 1.cssRem)
                    .backgroundColor(palette.surface)
                    .borderBottom(1.px, LineStyle.Solid, palette.border)
                    .overflow(Overflow.Auto)
                    .toAttrs()
            ) {
                Row(modifier = Modifier.gap(0.375.cssRem)) {
                    quickPrompts.forEach { qp ->
                        Div(
                            Modifier
                                .backgroundColor(palette.background)
                                .border(1.px, LineStyle.Solid, palette.border)
                                .padding(0.25.cssRem, 0.625.cssRem)
                                .borderRadius(1.cssRem)
                                .fontSize(0.75.cssRem)
                                .color(palette.textPrimary)
                                .cursor(Cursor.Pointer)
                                .onClick { onSendMessage(qp) }
                                .toAttrs()
                        ) {
                            SpanText(qp)
                        }
                    }
                }
            }

            // Messages List
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .flexGrow(1)
                    .padding(1.cssRem)
                    .gap(1.cssRem)
                    .overflow(Overflow.Auto)
            ) {
                if (messages.isEmpty()) {
                    Box(
                        modifier = Modifier.fillMaxWidth().padding(top = 3.cssRem),
                        contentAlignment = Alignment.Center
                    ) {
                        Column(horizontalAlignment = Alignment.CenterHorizontally, modifier = Modifier.gap(0.5.cssRem)) {
                            SpanText("🤖", Modifier.fontSize(2.cssRem))
                            SpanText(
                                s.chat.inputPlaceholder,
                                Modifier.fontSize(0.875.cssRem).color(palette.textSecondary)
                            )
                        }
                    }
                }

                messages.forEach { msg ->
                    ChatMessageBubble(
                        message = msg,
                        onApply = { onApplyMessage(msg.id) }
                    )
                }

                if (isLoading) {
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(0.75.cssRem)
                            .backgroundColor(palette.background)
                            .borderRadius(0.5.cssRem)
                            .border(1.px, LineStyle.Solid, palette.border)
                            .gap(0.5.cssRem),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        SpanText("✨", Modifier.fontSize(1.1.cssRem))
                        SpanText(s.chat.thinking, Modifier.fontSize(0.85.cssRem).color(palette.accent))
                    }
                }
            }

            // Input Box
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(1.cssRem)
                    .borderTop(1.px, LineStyle.Solid, palette.border)
                    .backgroundColor(palette.background)
                    .gap(0.625.cssRem)
            ) {
                Row(modifier = Modifier.fillMaxWidth().gap(0.5.cssRem), verticalAlignment = Alignment.Bottom) {
                    TextArea(
                        value = inputPrompt,
                        attrs = Modifier
                            .flexGrow(1)
                            .minHeight(3.5.cssRem)
                            .maxHeight(8.cssRem)
                            .padding(0.625.cssRem)
                            .backgroundColor(palette.surface)
                            .color(palette.textPrimary)
                            .border(1.px, LineStyle.Solid, palette.border)
                            .borderRadius(0.375.cssRem)
                            .fontSize(0.875.cssRem)
                            .resize(Resize.Vertical)
                            .toAttrs {
                                onInput { inputPrompt = it.value }
                                onKeyDown {
                                    if (it.key == "Enter" && !it.shiftKey) {
                                        it.preventDefault()
                                        if (inputPrompt.isNotBlank() && !isLoading) {
                                            val text = inputPrompt.trim()
                                            inputPrompt = ""
                                            onSendMessage(text)
                                        }
                                    }
                                }
                            }
                    )

                    AppButton(
                        text = s.common.send,
                        iconRight = "➤",
                        variant = AppButtonVariant.PRIMARY,
                        size = AppButtonSize.MD,
                        loading = isLoading,
                        enabled = inputPrompt.isNotBlank() && !isLoading,
                        onClick = {
                            val text = inputPrompt.trim()
                            if (text.isNotBlank()) {
                                inputPrompt = ""
                                onSendMessage(text)
                            }
                        }
                    )
                }
            }
        }
    }
}

@Composable
fun ChatMessageBubble(
    message: ChatMessage,
    onApply: () -> Unit
) {
    val theme = LocalAppTheme.current
    val s = strings()
    val palette = theme.colors
    val isUser = message.role == ChatMessageRole.USER
    val isSystem = message.role == ChatMessageRole.SYSTEM
    var showDiff by remember { mutableStateOf(true) }

    if (isSystem) {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .padding(0.5.cssRem)
                .backgroundColor(palette.statusPending.toRgb().copy(alpha = 25))
                .border(1.px, LineStyle.Dashed, palette.statusPending)
                .borderRadius(0.375.cssRem),
            contentAlignment = Alignment.Center
        ) {
            SpanText(message.content, Modifier.fontSize(0.8.cssRem).color(palette.textPrimary))
        }
        return
    }

    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = if (isUser) Arrangement.End else Arrangement.Start
    ) {
        Column(
            modifier = Modifier
                .maxWidth(85.percent)
                .padding(0.75.cssRem, 1.cssRem)
                .borderRadius(0.625.cssRem)
                .backgroundColor(if (isUser) palette.accent else palette.background)
                .color(if (isUser) Colors.White else palette.textPrimary)
                .border(1.px, LineStyle.Solid, if (isUser) palette.accent else palette.border)
                .gap(0.5.cssRem)
        ) {
            // Header
            Row(
                modifier = Modifier.fillMaxWidth().gap(0.5.cssRem),
                verticalAlignment = Alignment.CenterVertically
            ) {
                SpanText(
                    if (isUser) s.common.you else s.chat.aiAssistant,
                    Modifier.fontWeight(FontWeight.Bold).fontSize(0.8.cssRem)
                )
                if (message.revisionNumber != null && !isUser) {
                    SpanText(
                        s.chat.revisionLong(message.revisionNumber ?: 0),
                        Modifier
                            .fontSize(0.7.cssRem)
                            .color(if (isUser) Colors.White.copy(alpha = 180) else palette.textSecondary)
                    )
                }
            }

            // Message text
            SpanText(
                message.content,
                Modifier
                    .fontSize(0.875.cssRem)
                    .lineHeight(1.4)
            )

            // Diff Section if present
            val diff = message.diffUnified
            if (diff != null && diff.isNotBlank()) {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .margin(top = 0.5.cssRem)
                        .padding(0.5.cssRem)
                        .backgroundColor(palette.surface)
                        .borderRadius(0.375.cssRem)
                        .border(1.px, LineStyle.Solid, palette.border)
                        .gap(0.375.cssRem)
                ) {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        SpanText(
                            s.chat.diffTitle,
                            Modifier.fontSize(0.75.cssRem).fontWeight(FontWeight.SemiBold).color(palette.textPrimary).flexGrow(1)
                        )
                        AppButton(
                            text = if (showDiff) s.common.hide else s.common.show,
                            variant = AppButtonVariant.OUTLINE,
                            size = AppButtonSize.SM,
                            onClick = { showDiff = !showDiff }
                        )
                    }

                    if (showDiff) {
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .maxHeight(12.cssRem)
                                .overflow(Overflow.Auto)
                                .fontFamily("ui-monospace", "SFMono-Regular", "Menlo", "monospace")
                                .fontSize(0.75.cssRem)
                                .backgroundColor(palette.background)
                                .padding(0.5.cssRem)
                                .borderRadius(0.25.cssRem)
                        ) {
                            Column {
                                diff.lines().forEach { line ->
                                    val lineBg: Color = when {
                                        line.startsWith("+") && !line.startsWith("+++") -> palette.statusReady.toRgb().copy(alpha = 30)
                                        line.startsWith("-") && !line.startsWith("---") -> palette.statusFailed.toRgb().copy(alpha = 30)
                                        line.startsWith("@@") -> palette.accent.toRgb().copy(alpha = 30)
                                        else -> Colors.Transparent
                                    }
                                    val lineCol: Color = when {
                                        line.startsWith("+") && !line.startsWith("+++") -> palette.statusReady
                                        line.startsWith("-") && !line.startsWith("---") -> palette.statusFailed
                                        line.startsWith("@@") -> palette.accent
                                        else -> palette.textPrimary
                                    }
                                    Div(
                                        Modifier
                                            .fillMaxWidth()
                                            .backgroundColor(lineBg)
                                            .color(lineCol)
                                            .toAttrs()
                                    ) {
                                        SpanText(line.ifEmpty { " " })
                                    }
                                }
                            }
                        }
                    }

                    // Apply Button
                    Row(
                        modifier = Modifier.fillMaxWidth().margin(top = 0.25.cssRem),
                        horizontalArrangement = Arrangement.End,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        if (message.applied) {
                            SpanText(s.chat.changesApplied, Modifier.fontSize(0.75.cssRem).color(palette.statusReady).fontWeight(FontWeight.SemiBold))
                        } else {
                            AppButton(
                                text = s.chat.acceptChanges,
                                iconLeft = "✓",
                                variant = AppButtonVariant.PRIMARY,
                                size = AppButtonSize.SM,
                                onClick = onApply
                            )
                        }
                    }
                }
            }
        }
    }
}
